'use-strict';

import auth from 'src/services/auth';
import fcm from 'src/services/fcm';
import { v4 as uuidv4 } from 'uuid';
import webRtc from 'src/services/webrtc';

class BaseRTCConnection {
  static sdpStrings = {
    type: 'type',
    sdp: 'sdp'
  };
  static iceGatheringStates = {
    new: 'new',
    complete: 'complete',
    gathering: 'gathering'
  };
  // Answer polling starts fast and doubles up to the max interval.
  static answerPollInitialMs = 500;
  static answerPollIntervalMs = 2000;
  static answerMaxWaitMs = 30000;
  // ICE gathering: after the soft cap the offer goes out as soon as a
  // candidate of the best configured kind exists; after the hard cap it goes
  // out with whatever was gathered.
  static iceGatheringSoftCapMs = 2500;
  static iceGatheringHardCapMs = 6000;
  constructor(device, rtcConfiguration, connectionChangeCallback) {
    this._id = undefined;
    this.offer = undefined;
    this.offerStrings = undefined;
    this.transceiver = undefined;
    this.timeoutId = undefined;
    this.closed = false;
    this.answerDeadline = undefined;
    this.answerPollDelay = undefined;
    this.stopGathering = undefined;
    this.dispatchPromise = undefined;
    // Optional: called with a message when the connection fails after
    // connectToRemotePeer() has returned (e.g. the answer never arrives).
    this.onFailure = undefined;

    this.device = device;
    this.configuration = rtcConfiguration;
    this.connection = new RTCPeerConnection(this.configuration);

    if (connectionChangeCallback)
      this.connection.oniceconnectionstatechange = e =>
        connectionChangeCallback(this.connection.iceConnectionState);
  }
  async connectToRemotePeer() {
    await this.createOffer();
    if (this.closed) return;
    this.modifyOffer();
    this.dispatchPromise = this.dispatchOffer();
    await this.dispatchPromise;
    if (this.closed) return;
    this.answerDeadline = Date.now() + BaseRTCConnection.answerMaxWaitMs;
    this.answerPollDelay = BaseRTCConnection.answerPollInitialMs;
    await this.waitForAnswer();
  }
  async createOffer() {
    const localDescription = await this.connection.createOffer();
    await this.connection.setLocalDescription(localDescription);
    await this.gatherIceStates();
    if (this.closed) return;
    // Whatever has been gathered so far is already in localDescription.
    this.offer = {
      device: this.device,
      ...this.connection.localDescription.toJSON()
    };
  }

  // Candidate type worth waiting for: relay when a TURN server is configured
  // (a network that only works through TURN needs it), otherwise srflx when
  // a STUN server is, otherwise none.
  wantedCandidateType() {
    const urls = (this.configuration?.iceServers || []).flatMap(
      server => server?.urls || server?.url || []
    );
    if (urls.some(url => /^turns?:/i.test(url))) return 'relay';
    if (urls.some(url => /^stuns?:/i.test(url))) return 'srflx';
    return null;
  }

  // Resolves when gathering completes, or earlier: after the soft cap once a
  // wanted candidate exists, after the hard cap regardless, or on close().
  async gatherIceStates() {
    const { connection } = this;
    const { complete } = BaseRTCConnection.iceGatheringStates;
    if (this.closed || connection.iceGatheringState === complete) return true;

    const wanted = this.wantedCandidateType();
    return new Promise(resolve => {
      let softCapPassed = false;
      let hasWanted = false;
      let softTimer = undefined;
      let hardTimer = undefined;

      const finish = () => {
        clearTimeout(softTimer);
        clearTimeout(hardTimer);
        connection.removeEventListener('icegatheringstatechange', checkState);
        connection.removeEventListener('icecandidate', checkCandidate);
        this.stopGathering = undefined;
        resolve(true);
      };
      const checkState = () => {
        if (connection.iceGatheringState === complete) finish();
      };
      const checkCandidate = evt => {
        const candidate = evt.candidate;
        if (!candidate) return;
        const type =
          candidate.type ||
          / typ (\w+)/.exec(candidate.candidate || '')?.[1];
        if (!wanted || type === wanted) hasWanted = true;
        if (softCapPassed && hasWanted) finish();
      };

      connection.addEventListener('icegatheringstatechange', checkState);
      connection.addEventListener('icecandidate', checkCandidate);
      softTimer = setTimeout(() => {
        softCapPassed = true;
        if (hasWanted) finish();
      }, BaseRTCConnection.iceGatheringSoftCapMs);
      hardTimer = setTimeout(finish, BaseRTCConnection.iceGatheringHardCapMs);
      this.stopGathering = finish;
    });
  }

  //Override in base class to add custom args to offer
  modifyOffer() {}

  async dispatchOffer() {
    this.id = uuidv4();
    this.offer.id = this.id;
    this.offer.device_token = this.fcmToken;

    const opcua_tag_payload = {
      data: { device_token: this.offer.device_token },
      type: 'discover_devices_opcua',
      message_time: new Date().toISOString(),
      platform: 'portal',
      organization: this.organization,
      device_id: this.offer.device
    };
    await fcm.sendMessageToRabbitMQ(opcua_tag_payload, false);

    const payload = {
      data: this.offer,
      type: 'webrtc_stream_offer',
      message_time: new Date().toISOString(),
      platform: 'portal',
      organization: this.organization,
      device_id: this.offer.device
    };
    const [error, data] = await fcm.sendMessageToRabbitMQ(payload, false);
    if (error) throw 'Error while sending offer to remote peer';
  }

  // Polls for the remote answer. Reschedules itself, so failures are reported
  // through onFailure rather than thrown.
  async waitForAnswer() {
    if (this.closed) return;
    const [error, data] = await webRtc.getRTCOffer(this.id);
    // close() may have run, or the answer may have arrived over push, while
    // the request was in flight.
    if (this.closed || this.connection.signalingState !== 'have-local-offer')
      return;

    if (!error && data && Object.keys(data).length !== 0) {
      const { type, sdp } = data;
      const { answer } = this.offerStrings;
      if (type === answer) {
        try {
          await this.connection.setRemoteDescription({
            type: 'answer',
            sdp: sdp + '\r\n'
          });
        } catch (e) {
          if (!this.closed) this.fail('Error occurred connecting to remote peer');
        }
      }
    } else if (Date.now() >= this.answerDeadline) {
      this.fail('Timed out waiting for the device to answer');
    } else {
      this.timeoutId = setTimeout(
        this.waitForAnswer.bind(this),
        this.answerPollDelay
      );
      this.answerPollDelay = Math.min(
        this.answerPollDelay * 2,
        BaseRTCConnection.answerPollIntervalMs
      );
    }
  }

  fail(message) {
    clearTimeout(this.timeoutId);
    if (this.onFailure) this.onFailure(message);
  }

  async setRTCRemoteDescription(data) {
    console.log('In Media setRTCRemoteDescription', data);

    const { type, sdp } = data;
    const { answer } = this.offerStrings;

    if (type === answer) {
      await this.connection.setRemoteDescription({
        type: 'answer',
        sdp: sdp
      });
    }
  }

  async deleteRTCOffersFromDB() {
    const [error, data] = await webRtc.deleteRTCOffer(this.id);
    if (error) throw 'Error while deleting offers from Database';
  }

  close() {
    if (this.closed) return;
    this.closed = true;
    clearTimeout(this.timeoutId);
    if (this.stopGathering) this.stopGathering();
    this.onFailure = undefined;
    this.connection.oniceconnectionstatechange = null;
    this.connection.getReceivers().forEach(receiver => receiver.track?.stop());
    this.connection.close();
    this.notifyRemoteClose();
  }

  // Tells the device to stop and removes the stored offer. Nothing was sent
  // if no offer was dispatched; if one is still in flight, wait for it so the
  // close cannot overtake the offer.
  async notifyRemoteClose() {
    if (!this.dispatchPromise) return;
    try {
      await this.dispatchPromise;
    } catch (e) {
      // offer dispatch failed; still clean up below
    }
    const data = {
      id: this.id,
      device: this.device,
      sdp: '',
      type: 'close'
    };
    const payload = {
      data: data,
      type: 'webrtc_stream_offer',
      message_time: new Date().toISOString(),
      platform: 'portal',
      organization: this.organization,
      device_id: this.device
    };
    try {
      await fcm.sendMessageToRabbitMQ(payload, false);
      await this.deleteRTCOffersFromDB();
    } catch (e) {
      console.log('Error while closing remote stream', e);
    }
  }
}

export class MediaRTCConnection extends BaseRTCConnection {
  constructor(
    device,
    organization,
    fcmToken,
    video,
    rtcConfiguration,
    connectionChangeCallback = null
  ) {
    super(device, rtcConfiguration, connectionChangeCallback);

    this.fcmToken = fcmToken;
    this.organization = organization;
    this.offerStrings = {
      offer: 'offer',
      answer: 'answer'
    };
    this.video = video;
    this.addTrackEvent();
    this.connection.addTransceiver('video', { direction: 'recvonly' });
  }

  async dispatchOffer() {
    this.id = uuidv4();
    this.offer.id = this.id;
    this.offer.device_token = this.fcmToken;
    const payload = {
      data: this.offer,
      type: 'webrtc_stream_offer',
      message_time: new Date().toISOString(),
      platform: 'portal',
      organization: this.organization,
      device_id: this.offer.device
    };
    const [error, data] = await fcm.sendMessageToRabbitMQ(payload, false);
    if (error) throw 'Error while sending offer to remote peer';
    // this._id = data.id;
  }

  async setRTCRemoteDescription(data) {
    console.log('In Media setRTCRemoteDescription', data);

    // const [error, data] = await WebRTC.getRTCAnswer(this._id);
    // if (error) throw 'Error occurred connecting to remote peer.';

    const { type, sdp } = data;
    const { answer } = this.offerStrings;

    if (type === answer) {
      await this.connection.setRemoteDescription({
        type: 'answer',
        sdp: sdp
      });
    }
  }

  addTrackEvent() {
    const self = this;
    this.connection.addEventListener('track', evt => {
      if (evt.track.kind === 'video' && !self.closed) {
        self.video.srcObject = evt.streams[0];
      }
    });
  }

  close() {
    super.close();
    if (this.video) this.video.srcObject = null;
  }
}

export class RtcConnection extends BaseRTCConnection {
  constructor(
    device,
    organization,
    fcmToken,
    rtcConfiguration,
    connectionChangeCallback,
    defaultChannels = true
  ) {
    super(device, rtcConfiguration, connectionChangeCallback);
    this.organization = organization;
    this.fcmToken = fcmToken;
    this.offerStrings = {
      offer: 'offer-dc',
      answer: 'answer-dc'
    };
    this.channelNames = {
      fileChannel: 'fileChannel',
      jsonChannel: 'jsonChannel'
    };
    this.channels = {};

    if (defaultChannels)
      Object.keys(this.channelNames).forEach(name =>
        this.createDataChannel(name)
      );
  }

  async dispatchOffer() {
    this.id = uuidv4();
    this.offer.id = this.id;
    this.offer.device_token = this.fcmToken;
    const payload = {
      data: this.offer,
      type: 'webrtc_stream_offer',
      message_time: new Date().toISOString(),
      platform: 'portal',
      organization: this.organization,
      device_id: this.offer.device
    };
    const [error, data] = await fcm.sendMessageToRabbitMQ(payload, false);
    if (error) throw 'Error while sending offer to remote peer';
    // this._id = data.id;
  }

  async setRTCRemoteDescription(data) {
    console.log('In setRTCRemoteDescription', data);

    // const [error, data] = await WebRTC.getRTCAnswer(this._id);
    // if (error) throw 'Error occurred connecting to remote peer.';

    const { type, sdp } = data;
    const { answer } = this.offerStrings;

    if (type === answer) {
      await this.connection.setRemoteDescription({
        type: 'answer',
        sdp: sdp
      });
    }
  }

  modifyOffer() {
    if (!this.offer) throw 'Offer not yet created.';
    this.offer[BaseRTCConnection.sdpStrings.type] = this.offerStrings['offer'];
  }

  createDataChannel(channelName) {
    this.channels[channelName] = this.connection.createDataChannel(channelName);
  }
  getJSONChannel() {
    return this.channels[this.channelNames.jsonChannel];
  }
  getFileChannel() {
    return this.channels[this.channelNames.fileChannel];
  }
}
